import {SolarSystem} from "./SolarSystem";
import {ThrusterGlow} from "../ui/ThrusterGlow";
import {GameFrame} from "./GameFrame";
import {Gauge} from "../ui/Gauge";
import {gsap} from "gsap/all";
import $ from "jquery";

export class Game {

  constructor(app) {
    this.app = app;

    // How long the red "not aligned" flash stays before easing back to normal.
    this.flashErrorMs = 5000;
    // The flash stays up for flashErrorMs, but the player can try again after only
    // this long (so they aren't locked out for the whole flash).
    this.errorReenableMs = 1000;

    // How long the planets take to reach the meeting point. One clock for every
    // trip, whatever the launch turns out to be; it has to stay in step with
    // shipSpeed, which puts the ship there at the same moment. A run can still end
    // early, but only once the ship is off screen (see checkShipLeftView).
    this.transferSec = 20;

    // The ship covers this many SVG units of its path per second, and it never
    // speeds up or slows down. That's what makes the shape of the path matter for
    // more than looks: a straight shot to where Mars will be is a shorter trip than
    // a curve that follows the planet round, so it gets there sooner. Arriving is
    // not the point, arriving *when Mars does* is.
    // At 50 a launch has to travel about 1000 units to meet Mars on time, which the
    // straight shot (about 640) beats by seven seconds. Raise this and every trip
    // gets quicker, so the paths that rendezvous get longer and curvier. It's tied
    // to the path shape in SolarSystem.aimArc: making paths longer or curvier there
    // moves the travel distances, so check this still lands where you want after.
    // 50 is what makes the winning launch the half circle over the top of the sun.
    // Earth's start and Mars's arrival sit on opposite sides of the sun, so the
    // slower the ship the tighter the arc that still gets there in time, and a
    // tighter arc has to cut inside Earth's orbit to make the corner, which
    // isAimInsideEarthOrbit refuses. At 1000 units the one path that arrives on
    // time leaves Earth along its orbit and never dips under it.
    this.shipSpeed = 50;

    // There's no separate timing tolerance: the target circle is the tolerance. A
    // launch counts when the marker falls inside it, and the circle is sized from
    // the capture indicator (targetToleranceRadius), so widening that in game.scss
    // loosens the aim, the marker test and the tap window together.

    // Whether the launch that's flying will actually meet Mars (aimed through the
    // window, timed to arrive with it, and going the way Earth already is). Only a
    // rendezvous gets the capture challenge; anything else plays the trip out and
    // ends off course.
    this.rendezvous = false;

    // Which conclusion a failed launch has earned, decided at release. Null while a
    // rendezvous is still flying, since the capture challenge decides that one.
    this.launchConclusion = null;

    // Once a launch has failed, the planets keep orbiting and the ship keeps flying
    // for this long before the conclusion comes up. Nothing stops on a miss, so this
    // is just how long the player watches it carry on without them.
    this.failConclusionSec = 2.5;

    // Scales the ship's progress along its whole path into progress toward the
    // meeting point, so the capture cues fire relative to Mars rather than to a path
    // that runs well past it. Worked out at launch.
    this.approachProgressScale = 1;

    // Distance flown readout shown during the trip, in millions of miles (the readout
    // prints the M itself). It counts up from Earth to the full crossing as the ship
    // reaches Mars. Only a launch headed at Mars reads out at all; the rest get the
    // status message (see readsDistance).
    this.distanceFlown = {start: 0, end: 378};

    // How long the distance status spends CALCULATING before giving up and saying
    // OUT OF RANGE.
    this.distanceCalculatingSec = 5;

    // The distance-graphic ship rides the Earth->Mars line. Its art centers near
    // x 187.8 in the SVG; the line runs from x 21.4 (Earth) to 285.8 (Mars). We
    // translate the ship so its center sits on the line, Earth end to Mars end.
    this.distanceLine = {start: 21.44, end: 285.82, shipCenter: 187.81};

    // One timeline for the mission-stats value animations (counters, ship, readouts).
    this.statsTimeline = null;

    // Mission days and comm delay, which have to still be climbing when the scene
    // stops. Kept as records so a capture can hand them its own shorter clock partway
    // through (finishMissionCounters).
    this.missionCounters = [];

    // What the launch earned: "right" when the aimed path ended on Mars, "left"
    // when it drifted off course (a release with no aim at all falls short, so it
    // lands here too). Set when they launch, and later steps check it.
    this.chosenPath = null;

    // How far the drag counts for, from the press point, on each axis. Sideways
    // swings the launch direction round; up and down sets the force, which is what
    // decides how far and how straight the path goes. Dragging to the bottom of the
    // range is full force, to the top is none, and the press point itself sits
    // halfway.
    this.dragMax = 200;

    // How long the aim takes to catch up with the finger, see easeAimTo. Long
    // enough to swallow the jitter, short enough not to feel like lag.
    this.aimEaseSec = 0.15;

    // What a press has to do to count as an aim rather than a tap: it has to be
    // held this long, and the finger has to travel this far. Anything less is
    // called off, see cancelPress.
    this.minPressHoldMs = 300;
    this.minAimDragPx = 10;

    // A player still dragging this long after they started gets the target circle
    // on Mars's arrival spot as a hint: it fades in, goes full white while the
    // marker is inside it and turns solid on a launch that would meet Mars.
    this.targetRingHintSec = 0.25;
    this.targetRingHintCall = null;

    // The green zone of good launches shown with that hint. Nothing it depends on
    // changes between tries, so it is worked out once, see buildGoodZone.
    this.goodZoneBuilt = false;
    this.goodZoneTurnSteps = 200;
    this.goodZoneForceSteps = 100;

    // Whether a held finger has paused the screensaver (holdScreenSaver).
    this.screenSaverHeld = false;

    // Which way round the drag works, read from the JSON so the four directions
    // can be swapped about while testing without touching this file.
    this.aimControls = this.readAimControls();

    // How much of the flown trail fades out at its tip once the ship has landed,
    // and how long that takes. See SolarSystem.fadeTrailTip.
    this.trailTipFade = 0.05;
    this.trailTipFadeSec = 0.4;

    // When the press went down, whether it has become an aim yet, and the step it
    // moved the player off of so a cancel can put them back on it.
    this.pressStartMs = null;
    this.aimMoved = false;
    this.pressStep = null;


    // After a good press, the targets show the planet glow for this long, then
    // fade out gently.
    this.targetFadeMs = 2500;
    this.targetFadeTimeout = null;

    // Capture challenge: the player taps the target on Mars while the two of them
    // are together. These are only the heads-up cues, as fractions of the travel
    // toward Mars. Neither opens the tap window: that's the target circle, open
    // while Mars and the ship are both inside it (see isCaptureWindowOpen), so a
    // marker that sat dead centre earns a comfortable window and one that scraped
    // the rim barely a moment.
    this.captureConfig = {
      instructionAt: 0.5, // half-way to Mars: show the "prepare" instruction
      instructionMinSec: 5, // but never in the opening seconds of the flight
      showAt: 0.5,    // show the target (not tappable yet), with the instruction
    };

    // How long the ship's flight lasts, set at each launch. The cues below are
    // timed on it rather than on the clock, so a run held by the "still there?"
    // modal cannot let one of them through early.
    this.shipFlightSec = 0;
    // Capture run state (reset each launch): instruction shown, target shown,
    // window currently open, tapped in time, the window has closed unmade, and
    // whether the running-out-of-time warning has fired.
    this.captureInstructionShown = false;
    this.captureShown = false;
    this.captureArmed = false;
    this.captureResolved = false;
    this.captureWindowClosed = false;
    this.captureDangerShown = false;

    // The ship's size over a flight, as a multiplier of its CSS box in game.scss
    // (1 = the size it sits at on the Earth target). It holds that size while being
    // aimed, then grows to shipLaunchScale over shipScaleUpSec as it leaves Earth
    // and keeps it for the rest of the trip. Resizing the ship is a CSS change only.
    this.shipLaunchScale = 1.5;
    this.shipScaleUpSec = 2;

    // How long the thrusters fire on the way out of Earth. Runs a little past the
    // scale up above, so the engine is still lit as the ship settles into its
    // cruise rather than cutting out the instant it reaches full size. The speed
    // gauge tops out over the same time, and the engine goes out with it.
    this.shipLaunchBurnSec = 3;

    // The main trip timeline (planets + stats + gauges) runs on this delayed call;
    // it owns onTransferComplete so the trip ends at the same time for either arc.
    this.mainTween = null;

    // A successful landing holds on the finished scene for a beat before the
    // success conclusion fades in. This timer runs that wait so a reset can
    // cancel it.
    this.conclusionTimeout = null;

    // From the entry burn to the ship coming to rest on Mars. Fixed, not whatever
    // was left on the trip clock: a burn made late left almost no clock, so the
    // best-judged captures got the shortest landing.
    this.landingSec = 5;

    // The success glow on a burn bursts out of Mars like a click ripple: it grows
    // fast and slows down as it reaches its full size (scale 1) after growSec, and
    // fades out through that slowdown, from fadeOutAt (a fraction of growSec) to
    // the end.
    this.captureSuccessGlow = {
      growSec: 0.9,
      fadeInSec: 0.1,
      fadeOutAt: 0.35,
    };

    // What the ship shrinks to as it settles, as a multiple of its CSS size. Near
    // enough to nothing that it reads as dropping away onto the planet rather than
    // parking beside it; not quite 0, which pops out of existence.
    this.landingScale = 0.05;

    // Hold on the landed scene before the success conclusion. The whole ending runs
    // landingSec + this after the burn.
    this.successConclusionSec = 1;

    // Fuel plan for a run that reaches Mars, as fractions of a full tank. The gauge
    // starts full and comes down in three moves, each one lasting exactly as long as
    // what it pays for: the launch burn out of Earth orbit (shipLaunchBurnSec), a
    // slow drain over the coast (the rest of the trip clock), and the deorbit burn
    // onto Mars (landingSec). Whatever the three leave behind is what a good answer
    // finishes the game on, so these have to keep adding up to less than a full tank.
    this.fuelBurn = {
      launch: 0.25,
      cruise: 0.18,
      deorbit: 0.22,
    };

    // Fuel plan for a launch aimed against Earth's orbit (wrong-up-stream). Throwing
    // away the speed Earth was handing you makes the same burn cost far more, and
    // what's left goes fighting the planet, so the tank runs dry early. dryAt is
    // where it hits empty as a fraction of the trip clock, under 0.5 so the ship
    // coasts on with nothing in it for more than half the run.
    this.fuelBurnUpstream = {
      launch: 0.4,
      dryAt: 0.45,
    };

    // An upstream launch has lost the trip the moment its tank runs dry, so the run
    // ends this long after that rather than playing out the whole trip clock.
    this.upstreamDryConclusionSec = 1;

    // How an ending drawing built out of .ending-part-N groups comes in: the
    // picture holds empty for endingPartDelaySec, then each group fades over
    // endingPartFadeSec, and the next one starts endingPartStaggerSec after the one
    // before it.
    this.endingPartDelaySec = 1;
    this.endingPartFadeSec = 0.5;
    this.endingPartStaggerSec = 1;

    // Half the ship's box, near enough: its art is about 63 by 50 at cruise size.
    // Used to check the ship's corners rather than its centre, so the run is called
    // off once the last of it has been clipped away, not when it's half showing.
    this.shipOffscreenMarginPx = 40;

    // Set once a run has been wrapped up early, and the conclusion waiting on a
    // timer (null when none is).
    this.runEnded = false;
    this.pendingConclusion = null;

    this.gameFrame = new GameFrame();

    this.solarSystem = new SolarSystem(app, {
      host: $(".solar-system"),
      cycleDuration: 10,
      startAngles: {earth: 90, mars: 45},
      alignRanges: {earth: [75, 105], mars: [35, 55]},
      debug: false
    });

    // Where the orbit loop picks up on a "try again", as progress through its
    // cycle. The planets line up at the start of each cycle, which a try again
    // lands on while the game is still fading in, so starting from 0 would leave
    // the player waiting a whole cycle (Earth's two laps). Halfway means Earth has
    // already done one lap and the next alignment is half a cycle away.
    this.tryAgainCycleProgress = 0.5;

    // The thrusters. They live inside the ship box, so they need nothing from
    // here beyond being told when the engine lights and when it goes out. This is
    // the pending end of a full burn.
    this.thrusterGlow = new ThrusterGlow($(".ship"));
    this.thrusterBurn = null;

    this.fuelGauge = new Gauge($(".fuel-gauge"));
    this.speedGauge = new Gauge($(".speed-gauge"));

    // Tap anywhere on the game area to check alignment.
    this.acceptPress = false;
    // True once the player locks in a good alignment. Launched flips true when the
    // trip fires on release, so it only runs once.
    this.locked = false;
    this.launched = false;

    // Keep the bound versions so we can remove the same handlers later with off().
    this.onPress = this.onPress.bind(this);
    this.onRelease = this.onRelease.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onCapturePress = this.onCapturePress.bind(this);

    this.flashTimeline = null;

    // The one finger we're tracking. We lock onto the first one down and ignore
    // any others, so a second finger can't mess with the aim. Null means no finger.
    this.activePointerId = null;

    // Drag state: where the press started, how far round the launch direction has
    // been swung (-1 to 1, a full swing either way from its axis), which way that
    // points (a unit vector in screen axes, y down) and the force, 0 to 1.
    this.dragOriginX = null;
    this.dragOriginY = null;
    this.aimTurn = 0;
    this.aimVector = null;
    this.aimPercentage = 0;
    this.aimTween = null;
  }

  // The force a press launches at before it is dragged either way. The press point
  // sits in the middle of the force range, so the path shows at half force the moment
  // they press and moves from there.
  restingForce() {
    return 0.5;
  }

  // The window the launch has to land in to count as on target: the capture
  // indicator's own circle, read off the element so it follows game.scss. What the
  // player aims at is exactly the disc they later have to tap.
  targetToleranceRadius() {
    const w = $(".capture-touch-indicator").outerWidth();
    return (w || 157) / 2;
  }

  // showFrame reveals the game frame with its staggered intro. On a "try again"
  // the frame is already up and should stay put, so it's skipped there.
  // cycleProgress starts the orbit loop part way through (see tryAgainCycleProgress).
  start({showFrame = true, cycleProgress = 0} = {}) {
    if (showFrame) this.gameFrame.show();
    // Step 1 has no restart button. It appears once the player reaches step 2.
    // On a fresh entry it's not up yet, so hide it instantly; on a "try again" it's
    // visible and should animate out (showFrame is false there).
    this.gameFrame.hideRestart(showFrame);

    // A restart can land mid-trip or on the conclusion, so stop every launch/
    // conclusion fade before rebuilding. A lingering tween would fight the reset
    // below (gsap.set doesn't stop a running tween on the same target). The solar
    // system, gauges and flash timeline are stopped by restart()/setLevel/
    // clearStates just after.
    gsap.killTweensOf($(".left-info-display, .right-info-display, .speed-gauge, .mission-stats, .mission-stats > *, .conclusions [data-conclusion], .planet-target, .step, .solar-system, .ending-graphics, .ending-graphics [class*='ending-part-']"));

    // Start a fresh orbit each time we enter (rebuilds the loop, clears the last
    // trip). The targets and ghosts can only be placed once the screen is laid out.
    this.solarSystem.restart(cycleProgress);
    if (!this.goodZoneBuilt) this.buildGoodZone();
    this.fuelGauge.setLevel(1, false); // full
    this.speedGauge.setLevel(0, false); // 0 km/s
    gsap.set(this.speedGauge.el, {autoAlpha: 0});
    gsap.set($(".mission-stats"), {autoAlpha: 0}); // shown with the speed gauge at launch
    gsap.set($(".planet-target"), {autoAlpha: 0});
    // Restore the info displays and hide the conclusions layer in case we're
    // coming back from a finished trip.
    $(".conclusions").hide();
    gsap.set($(".left-info-display, .right-info-display"), {clearProps: "opacity,visibility"});
    // Bring the solar system back and hide the success ending graphic (a good trip
    // swaps one for the other).
    gsap.set($(".solar-system"), {clearProps: "opacity,visibility"});
    gsap.set($(".ending-graphics"), {autoAlpha: 0});
    // The parts of an ending drawing keep the alpha their reveal left behind, so
    // clear it or a replay would show them already up.
    gsap.set($(".ending-graphics [class*='ending-part-']"), {clearProps: "opacity,visibility"});
    this.prepareTrajectories();
    this.resetMissionStats(); // counters, ship and trail back to their start
    this.clearStates();

    // Wire up the tap handler (remove it first so it can't double up). Release and
    // move live on the window so the drag keeps tracking past the edges of the game.
    $(".game").off("pointerdown", this.onPress).on("pointerdown", this.onPress);
    $(window)
      .off("pointerup pointercancel", this.onRelease)
      .on("pointerup pointercancel", this.onRelease)
      .off("pointermove", this.onMove)
      .on("pointermove", this.onMove);
  }

  stop() {
    this.gameFrame.hide();

    $(".game").off("pointerdown", this.onPress);
    $(window).off("pointerup pointercancel", this.onRelease).off("pointermove", this.onMove);
    this.acceptPress = false;
    this.activePointerId = null;

    if (this.flashTimeline) {
      this.flashTimeline.kill();
      this.flashTimeline = null;
    }
    this.killMainTween();
    this.solarSystem.killShipFlight();
    // Leaving mid-landing has to take the landing with it: its completion schedules
    // the success ending, which would then land on a screen nobody is looking at.
    this.solarSystem.killAngleTweens();
    this.clearConclusionTimeout();
    this.killThrusterBurn();
    this.thrusterGlow.reset(); // stop the canvas ticker when we leave the game
    this.clearTargetFade();
    this.hideCaptureTarget();
    this.hideCaptureBlur();
    this.hideTouchIndicator();
    this.hideHandcoach(false);
    this.cancelTargetRingHint();
    this.holdScreenSaver(false);
    this.solarSystem.hideAimPath();
    this.solarSystem.showDestination(false);
  }

  initStep(stepId) {
    console.log("game initStep", stepId);
    if (stepId === 2) {
      // Bring the targets in so the player can start aligning the planets. The
      // container is shown at full opacity right away (a GSAP opacity fade on
      // it would suppress the lens backdrop-filter until the fade ends, see
      // refreshGlass), then the glass is refreshed and only the crosshair SVG
      // is faded in. That keeps the glass effect active for the whole reveal.
      gsap.set($(".planet-target"), {autoAlpha: 1});
      this.refreshGlass($(".planet-target-lens"));
      gsap.fromTo($(".planet-target-svg"),
        {autoAlpha: 0},
        {
          autoAlpha: 1,
          duration: 0.5,
          ease: "power1.out"
        }
      );
      // The ship is the Earth target's center icon, so bring it in with them: park
      // it on that center at its resting size, where it stays until launch.
      this.solarSystem.positionShip();
      $(".solar-system").addClass("ship-idle");

      // The hand shows what to do from the moment the step is up, until the first
      // press (onPress).
      this.showHandcoach();

      // Show the instructions, keep the "wrong" retroaction hidden until a miss.
      gsap.set($(".step-instructions"), {autoAlpha: 1});
      gsap.set($(".step-retroaction-wrong").removeClass("hidden"), {
        autoAlpha: 0,
        y: 20,
        yPercent: -50
      });

    } else if (stepId === 3) {
      this.gameFrame.showRestart();

    }
  }

  // Runs once a step has fully faded in. Presses to align are only accepted here,
  // not in initStep: initStep fires mid-transition (right after the fade-out), so
  // enabling presses there let a fast tap land between steps and mess up the UI.
  afterStep(stepId) {
    if (stepId === 2) {
      this.acceptPress = true;
    }
  }

  // When the player presses: show the touch feedback, then check alignment. If
  // they're lined up, ease the planets onto the targets and mark them. If not,
  // flash red.
  onPress(e) {
    if (!this.acceptPress) return;

    // Ignore extra fingers — we only follow the first one down until it lifts.
    if (this.activePointerId !== null) return;
    this.activePointerId = e.originalEvent.pointerId;
    this.holdScreenSaver(true);

    // They've had a go, so the hand hint has done its job, aligned or not.
    this.hideHandcoach();

    this.showTouchIndicator(e);

    if (this.solarSystem.isAligned()) {
      // A correct press. If a wrong-press error flash is still on screen (presses
      // re-enable before it eases off), stop it and clear the red state first.
      this.clearErrorFlash();

      this.acceptPress = false; // locked in
      this.locked = true;
      // Show the planet glow now (it stays while pressed) and slide the planets
      // onto the exact target centers. The targets fade out X seconds later (or
      // immediately on release, whichever comes first).
      $(".planet-target").addClass("aligned");
      this.solarSystem.snapToStart();
      this.clearTargetFade();
      this.targetFadeTimeout = setTimeout(() => {
        this.fadeTargetsOut();
        this.targetFadeTimeout = null;
      }, this.targetFadeMs);

      // Start aiming: remember where the press started, show the window they have
      // to land in, and draw the path straight away at its resting aim, so there's
      // something to move rather than a blank screen until they drag.
      this.dragOriginX = e.pageX;
      this.dragOriginY = e.pageY;
      this.pressStartMs = millis();
      this.aimMoved = false;
      this.aimTurn = 0;
      this.aimPercentage = this.restingForce();
      $(".solar-system").addClass("aiming");
      this.solarSystem.setDestinationRadius(this.targetToleranceRadius());
      this.solarSystem.setRendezvousTravel(this.rendezvousTravelDistance());
      this.updateAim();

      // Lined up, so move the player on to the next step, which is the one
      // telling them to slide and then lift. They need it from the moment they
      // are down, so it cannot wait for them to start sliding. Where they came
      // from is remembered here, since a press that comes to nothing goes back
      // there, see cancelPress.
      this.pressStep = this.app.currentStep;
      this.app.goToNextStep();
    } else {
      this.flashError();
    }
  }

  // While aiming (pressed and locked, before launch): the two axes do different
  // jobs. One swings the direction the ship leaves in, right round if they drag
  // far enough; the other sets the force, which is how far and how straight the
  // path goes. Which axis does which, and which end of it means what, comes from
  // the JSON, see readAimControls. The touch dot follows the finger, held inside
  // the drag range on each axis so it reads as the limit of both.
  onMove(e) {
    if (e.originalEvent.pointerId !== this.activePointerId) return; // only our finger
    if (!this.locked || this.launched || this.dragOriginX == null) return;

    const dx = this.clampDrag(e.pageX - this.dragOriginX);
    const dy = this.clampDrag(e.pageY - this.dragOriginY);

    const $ind = $(".touch-indicator");
    if ($ind.length) {
      const offset = $ind.offsetParent().offset() || {left: 0, top: 0};
      $ind.css({
        left: `${this.dragOriginX + dx - offset.left}px`,
        top: `${this.dragOriginY + dy - offset.top}px`,
      });
    }

    // Far enough from where the finger went down to count as aiming, so a hand
    // resting still on the glass is not mistaken for one.
    if (!this.aimMoved && Math.hypot(dx, dy) >= this.minAimDragPx) {
      this.aimMoved = true;
      this.scheduleTargetRingHint();
    }

    const controls = this.aimControls;
    const turnDrag = controls.directionOnVertical ? dy : dx;
    const forceDrag = controls.directionOnVertical ? dx : dy;

    this.easeAimTo(
      (controls.invertDirection ? -turnDrag : turnDrag) / this.dragMax,
      this.forceFromDrag(controls.invertForce ? -forceDrag : forceDrag)
    );
  }

  // How a drag maps to the two things it does, from content.game.aim_controls in
  // the JSON:
  //
  //   "aim_controls": {
  //     "direction_on_vertical": true,   up and down swing the launch direction
  //                                      and left and right set the force. false
  //                                      puts them the other way round.
  //     "invert_direction": true,        which end of that axis swings which way
  //     "invert_force": true             which end of the other means more force
  //   }
  //
  // Between them those three cover every way round the four directions can be put.
  // Anything left out keeps the value here.
  readAimControls() {
    const controls = (this.app.data && this.app.data.content.game.aim_controls) || {};
    const pick = (name, fallback) => (controls[name] !== undefined ? controls[name] : fallback);

    return {
      directionOnVertical: pick("direction_on_vertical", true),
      invertDirection: pick("invert_direction", true),
      invertForce: pick("invert_force", true)
    };
  }

  // Moves the aim to where the finger is asking for, over a moment rather than
  // straight away. The path is long, so a turn of the aim moves its far end much
  // further than its base: a pixel of jitter under a still finger throws the tip
  // of the arc across the screen and the whole line twitches. Easing there instead
  // of jumping smooths that out, and it is short enough that the arc still feels
  // stuck to the finger.
  //
  // Held in aimTween so a launch, a reset or the end of a flight can stop it: what
  // is on screen when the finger lifts is what launches, see onRelease.
  easeAimTo(turn, force) {
    this.aimTween = gsap.to(this, {
      aimTurn: turn,
      aimPercentage: force,
      duration: this.aimEaseSec,
      ease: "power2.out",
      overwrite: true,
      onUpdate: () => {
        this.aimVector = this.solarSystem.aimVectorFor(this.aimTurn);
        this.updateAim();
      }
    });
  }

  // Hold a drag offset inside the range that counts, either way from the press.
  clampDrag(v) {
    return Math.max(-this.dragMax, Math.min(this.dragMax, v));
  }

  // The force a drag along the force axis makes, 0 to 1: one way for more, the
  // other for less, with the press point sitting at the resting force so there's
  // room to go both ways. Which way is which is a JSON setting, see
  // readAimControls, so what arrives here is already the right way round.
  //
  // It reads the same way whichever way the ship is pointing, on purpose. Flipping
  // it with the heading, so it always felt like pulling back against the launch,
  // would mean the axis inverting under the player's finger part way through a
  // drag, which is worse than an axis that always means the same thing.
  forceFromDrag(drag) {
    return Math.max(0, Math.min(1, this.restingForce() * (1 + drag / this.dragMax)));
  }

  // Redraw the path for the current aim, point the ship along it, and show on the
  // destination ring how good the launch is: whether the ship will be in the window
  // when Mars is, and whether letting go right now would actually work. The solid
  // ring runs off the same resolveLaunch the release does, so it can never promise a
  // rendezvous the release then refuses.
  updateAim() {
    if (!this.aimVector) return;
    const geo = this.solarSystem.aimGeometry(this.aimVector.x, this.aimVector.y, this.aimPercentage);
    this.solarSystem.setAimPath(geo);
    // Which way the ship sets off decides the trip on its own. Whether that gets
    // said out loud is the solar system's business (setAimUpstream), not this one's:
    // here we just report the aim.
    this.solarSystem.setAimUpstream(this.isAimUpstream());
    // The ring reads off the marker, not off the path: most right-hand aims put the
    // path through the window and not one of them can meet Mars, so lighting up for
    // those told the player they were nearly there while they were nowhere near.
    this.solarSystem.showDestination(true, this.isAimOnSchedule(), this.resolveLaunch().rendezvous);
    // The ship stays centered on the Earth target and only rotates to point along
    // the path it would fly; size never changes.
    this.solarSystem.setShipAim("aim");
  }

  // True when the path passes through the target window at some point on its way
  // through, not where it finally runs out. Says nothing about whether the launch
  // works (isAimOnSchedule does that): it only tells a near miss from a wild one.
  isAimOnTarget() {
    const near = this.solarSystem.closestApproachToMars();
    if (!near) return false;
    return near.distance <= this.solarSystem.pxToSvg(this.targetToleranceRadius());
  }

  // How far the ship gets before Mars reaches the meeting point. Speed by time, and
  // it doesn't change while aiming: what moves is the path under it.
  rendezvousTravelDistance() {
    return this.shipSpeed * this.marsArrivalSec();
  }

  // When Mars gets to the meeting point: the end of the transfer, which is what the
  // whole trip timeline is built around.
  marsArrivalSec() {
    return this.transferSec;
  }

  // True when the ship will be inside the target circle at the moment Mars is there,
  // which is what the marker on the path shows. Same circle and same test that later
  // opens the tap window, so the marker in the ring is a promise, not a hint.
  isAimOnSchedule() {
    return this.solarSystem.isInDestination(this.solarSystem.rendezvousPoint());
  }

  // True when the path cuts inside Earth's orbit on its way to Mars. Earth's orbit
  // is the floor of the trip: the ship starts on it with Earth's speed already in
  // hand, so a path that dives under it is dropping toward the sun rather than
  // climbing out to Mars, however neatly it comes back up onto the target. Only the
  // stretch flown before Mars arrives counts; where the path goes afterwards is
  // nobody's business.
  isAimInsideEarthOrbit() {
    const geo = this.solarSystem.aimGeo;
    if (!geo) return false;
    return !this.solarSystem.staysOutsideEarthOrbit(geo, this.rendezvousTravelDistance());
  }

  // Whether a launch aimed this way, at this force, would meet Mars: the same
  // questions resolveLaunch asks, put to an aim that isn't the one on screen. The
  // debug band walks the whole drag through this, so what it draws is exactly what
  // the game accepts.
  aimWouldRendezvous(aim, force) {
    if (!aim || aim.y > 0) return false; // upstream is refused however well aimed
    const travel = this.rendezvousTravelDistance();
    const geo = this.solarSystem.aimGeometry(aim.x, aim.y, force);
    if (!this.solarSystem.isInDestination(this.solarSystem.svgPointAlong(geo, travel))) return false;
    return this.solarSystem.staysOutsideEarthOrbit(geo, travel);
  }

  // True when the launch is aimed into the bottom half, which from Earth's start
  // spot means flying against the way Earth is already travelling round the sun.
  // Going upstream means throwing away the orbital speed Earth is handing you for
  // free, so the trip was never on however well it's aimed.
  //
  // Whether this can ever be true depends on SolarSystem.aimSweepDeg, which has to
  // stay above 90 for it: at 90 or below the drag can only point the ship at or
  // above the horizontal, so no launch is ever upstream and the ending it leads to
  // can never happen. At the current 110 it's the outer fifth of the drag either way.
  isAimUpstream() {
    return !!this.aimVector && this.aimVector.y > 0;
  }

  // What a launch has earned. Three questions, asked in this order:
  //
  //   misses the circle              -> wrong-trajectory, whichever way it was aimed
  //   makes the circle, downward     -> wrong-up-stream
  //   makes the circle, cuts inside  -> wrong-trajectory
  //   makes the circle, over the top -> rendezvous; the capture challenge decides
  //
  // The circle comes first because it's the bigger miss. Direction only earns its
  // own ending once the aim was otherwise good enough to have worked. It comes back
  // as `upstream` either way, since fighting the orbit costs the same fuel whether or
  // not that's the headline. Cutting inside Earth's orbit comes last because it's
  // the fine one: the path lands on the target and reads as a hit right up until
  // you follow where it went to get there.
  resolveLaunch() {
    const reachesTarget = this.isAimOnSchedule();
    const upstream = this.isAimUpstream();

    if (!reachesTarget) {
      // How close it came still picks the readouts: one that crosses the window does
      // close on Mars, so its distances read as arriving even though it turns up to
      // an empty spot. One that never comes near reads as drifting away.
      const dir = this.isAimOnTarget() ? "right" : "left";
      return {conclusion: "wrong-trajectory", dir, rendezvous: false, upstream};
    }

    if (upstream) {
      // It arrives, so it gets the arriving readouts. What's wrong is the direction:
      // coming round the wrong way it meets Mars head-on, far too fast relative to
      // the planet to be captured, so it sails through and out the far side.
      return {conclusion: "wrong-up-stream", dir: "right", rendezvous: false, upstream};
    }

    if (this.isAimInsideEarthOrbit()) {
      // It gets to the right spot at the right moment, but by dropping under Earth's
      // orbit first. That's a fall toward the sun with a lucky landing, not a
      // transfer, so it reads as arriving on the wrong path.
      return {conclusion: "wrong-trajectory", dir: "right", rendezvous: false, upstream};
    }

    // Aimed to be there when Mars is, going the way Earth already is, and never
    // under Earth's orbit. Nothing is decided yet: the capture challenge picks
    // between success and failed.
    return {conclusion: null, dir: "right", rendezvous: true, upstream};
  }

  // Show the glass touch dot at the press point. It stays until they let go.
  showTouchIndicator(e) {
    const $ind = $(".touch-indicator");
    if (!$ind.length) return;

    // pageX/pageY and offset() are both measured from the document, so they line up.
    const offset = $ind.offsetParent().offset() || {left: 0, top: 0};
    $ind.css({
      left: `${e.pageX - offset.left}px`,
      top: `${e.pageY - offset.top}px`,
    }).addClass("active");
  }

  // Hide the touch dot when they let go. If they locked in a good alignment,
  // releasing launches the planets from the start spot to the target (once),
  // hides the glow, and ramps the gauges over the trip.
  onRelease(e) {
    if (e.originalEvent.pointerId !== this.activePointerId) return; // only our finger
    this.activePointerId = null;
    this.holdScreenSaver(false);
    this.app.resetTimer();

    this.hideTouchIndicator();
    // Whatever the release does next (launch or call off), the hint is over.
    this.cancelTargetRingHint();

    if (this.locked && !this.launched) {
      // A tap, or a press that never moved, is not an aim. Launching one sends the
      // ship off on the resting aim the player never chose, so it is called off
      // instead and they line the planets up again.
      if (!this.aimMoved || millis() - this.pressStartMs < this.minPressHoldMs) {
        this.cancelPress();
        return;
      }

      this.launched = true;

      // The aim stops where it is drawn, so the launch flies the path the player
      // was looking at rather than one the ease was still travelling towards.
      if (this.aimTween) {
        this.aimTween.kill();
        this.aimTween = null;
      }

      this.clearTargetFade();
      // Turn the glow off and fade the targets out now (in case the 1s timer had
      // not fired yet). They stay hidden until the next reset.
      $(".planet-target").removeClass("aligned");
      this.fadeTargetsOut();

      this.solarSystem.showDestination(false);
      this.launchTransfer(this.resolveLaunch());

      // Hide speed and legend after the first launch.
      gsap.to($(".top-bar"), {alpha: 0});

      return;
    }

    this.clearAim(); // ship and arcs go away on any release that didn't launch

  }

  // Undoes a press that never became an aim and puts the player back to lining the
  // planets up. Everything the press did is taken back: the lock, the glow on the
  // targets, the aim scaffolding, and the planets, which start going round again
  // from the alignment they were snapped to.
  //
  // Going back to the step the press came from puts the rest right on its own: it
  // is the step that shows the targets, parks the ship on Earth and takes presses
  // again, see initStep and afterStep.
  //
  // It is asked for by number rather than by stepping back, because the step
  // change the press started is very likely still playing: half way through it no
  // step is marked current at all, and working the number out from the markup then
  // lands on the wrong one. That same transition holds the interaction lock, which
  // the step machine refuses to move under, so the lock is dropped here first. It
  // takes it straight back on its own way through.
  cancelPress() {
    this.locked = false;
    this.pressStartMs = null;
    this.aimMoved = false;

    this.clearTargetFade();
    $(".planet-target").removeClass("aligned");
    this.clearAim();
    this.solarSystem.resumeOrbit();

    this.app.unlockInteraction();

    // Nothing to fade out: what is leaving is either the step being returned to
    // or, once the swap has run, nothing at all.
    this.app.goToStep(this.pressStep, null, {skipOut: true});
  }

  // Run the trip: planets sweep round toward the meeting point, gauges change, and
  // the ship flies the path that was aimed. `result` comes from resolveLaunch: its
  // dir picks how the trip reads ("right" for the full transfer, "left" for the
  // short off-course one), and rendezvous says whether the capture challenge is
  // still on the table. Clears the aim once the flight finishes.
  launchTransfer({dir, rendezvous, conclusion, upstream}) {
    this.chosenPath = dir; // the outcome we went with — later steps check it
    this.rendezvous = rendezvous;
    this.launchConclusion = conclusion;
    const transferSec = this.transferSec;
    // What the launch earned, which is all the gauges and stats read off. Direction
    // is not in here: it only decides which way the arc curves, not how any of this
    // plays out.
    const result = {rendezvous, upstream};
    // The planets carry on turning past the meeting point for as long as the
    // player will still be watching, so nothing freezes on a rendezvous that didn't
    // happen. A successful capture settles them on target instead (onCapturePress).
    // On a rendezvous that tail has to cover the second half of the capture window
    // too: it stays open until Mars clears the circle, which is after the transfer
    // clock has already run out, and the failed ending is timed from there.
    const tailSec = this.failConclusionSec
      + (rendezvous ? this.solarSystem.marsDestinationExitSec(transferSec) : 0);
    this.solarSystem.animateToTarget(null, transferSec, tailSec);

    // The aiming step is done, so fade it out and bring the speed gauge and
    // mission stats in so the trip reads off them.
    this.hideCurrentStep();
    this.showSpeedGauge();
    this.showMissionStats();
    // The graphic runs on the trip clock, but the counters run out to where the scene
    // really stops, which is the same tail the planets keep turning for. A capture
    // stops sooner and reschedules them (onCapturePress).
    this.animateMissionStats(result, transferSec, transferSec + tailSec);

    // How the gauges move is the clearest read on what the launch did wrong, so each
    // ending has its own plan.
    this.burnFuelForTransfer(result, transferSec);
    this.speedUpForTransfer(transferSec);

    // Fresh capture run: no instruction or target up, nothing tapped, no warning
    // yet, and clear any instruction left over from a previous trip.
    this.captureInstructionShown = false;
    this.captureShown = false;
    this.captureArmed = false;
    this.captureResolved = false;
    this.captureWindowClosed = false;
    this.captureDangerShown = false;
    this.runEnded = false;
    this.pendingConclusion = null;
    this.hideMissionInstructions();

    // Running dry going upstream is the end of that trip, so don't make the player
    // watch the rest of it.
    if (upstream) this.concludeWhenDry(this.upstreamDrySec(transferSec));

    // Main timeline: the planets, stats and gauges above all run over transferSec.
    // This tween is their shared clock and owns the trip's completion, so it fires at
    // the same time for either arc, once they've reached their end.
    this.killMainTween();
    this.mainTween = gsap.to({}, {
      duration: transferSec,
      onComplete: () => {
        this.mainTween = null;
        this.onTransferComplete();
      },
    });

    // Ship timeline: separate from the trip's, because it's the path's own length
    // that says how long the ship takes, not the schedule. It flies at one steady
    // speed the whole way, so the duration is just distance over speed.
    // Nothing ever stops it: it flies the whole path, straight through the meeting
    // point whether Mars is there or not, and on out of the system. The only thing
    // that takes it off the path is a successful capture, which kills this flight
    // and pulls the ship onto the planet (see onCapturePress).
    const approach = this.solarSystem.closestApproachToMars();
    const pathLength = this.solarSystem.aimPathLength();
    // The capture cues are timed against reaching Mars, not against the far end of
    // the path, so keep what turns one into the other.
    this.approachProgressScale = approach && approach.length ? pathLength / approach.length : 1;
    const landing = {
      launchScale: this.shipLaunchScale, // grows to this as it pulls away from Earth
      scaleUpSec: this.shipScaleUpSec,   // and takes this long to get there
    };
    const shipSec = Math.max(0.1, pathLength / this.shipSpeed);
    this.shipFlightSec = shipSec;

    // Engine full for the launch burn, then out once top speed is reached, so the
    // ship coasts the rest of the way with the engine off. Only a successful capture
    // lights it again (onCapturePress).
    this.burnThrusters(this.shipLaunchBurnSec);

    // Work out what counts as visible now, while the layout is settled, so the
    // flight can tell when the ship has gone.
    this.solarSystem.cacheVisibleShape();

    this.solarSystem.flyShipAlongArc("aim", shipSec,
      () => this.finishFlight(), // tidy up the aiming scaffolding once it's done
      (t) => this.onTransferProgress(t),
      landing
    );
  }

  // Speed over the trip out. The climb is the launch burn made visible, so it runs
  // for as long as the thrusters are lit and no longer, whatever the launch turns
  // out to be. From there it holds at full speed, since nothing out there slows a
  // coasting ship down. Only a successful deorbit burn brings it back to 0
  // (onCapturePress).
  speedUpForTransfer(transferSec) {
    const climbSec = Math.min(this.shipLaunchBurnSec, transferSec);

    // Softly out of the climb, since the engine tops the ship out rather than
    // cutting dead.
    this.speedGauge.setLevel(1, true, climbSec, "power1.out");
  }

  // Fuel over the trip out. Every plan opens with a burn lasting exactly as long as
  // the thrusters are lit, so the gauge and the engine always agree, and they differ
  // in what that burn costs and what's left to coast on:
  //
  //   upstream    -> a much bigger burn, then empty before the trip is half run,
  //                  whether the aim was otherwise good (wrong-up-stream) or not
  //   rendezvous  -> a measured burn, then a slow coast, holding back what the
  //                  deorbit burn will need (spent in onCapturePress)
  //   off target  -> the good run's burn, then a steady drain that reaches empty
  //                  just as the trip clock runs out
  //
  // Direction is asked first: fighting the orbit costs what it costs, and a launch
  // that did that has already lost the trip on fuel alone.
  burnFuelForTransfer(result, transferSec) {
    const launchSec = Math.min(this.shipLaunchBurnSec, transferSec);
    const [burn, cruise] = this.fuelLegs(result, transferSec, launchSec);

    this.fuelGauge.sequence([burn, cruise]);
  }

  // The two legs a trip's fuel comes off in: the launch burn, then the coast. Both
  // are {level, duration} for the gauge, the level being what's left when that leg
  // is done.
  fuelLegs({rendezvous, upstream}, transferSec, launchSec) {
    if (upstream) {
      const drySec = this.upstreamDrySec(transferSec);
      return [
        {level: 1 - this.fuelBurnUpstream.launch, duration: launchSec},
        {level: 0, duration: drySec - launchSec},
      ];
    }

    if (rendezvous) {
      return [
        {level: this.fuelLeftAfter("launch"), duration: launchSec},
        {level: this.fuelLeftAfter("launch", "cruise"), duration: transferSec - launchSec},
      ];
    }

    // Aimed the right way round but off target: it leaves Earth on the same burn a
    // good run makes, so nothing looks wrong yet, then spends everything it had held
    // for the arrival on a path that was never going to meet Mars.
    return [
      {level: this.fuelLeftAfter("launch"), duration: launchSec},
      {level: 0, duration: transferSec - launchSec},
    ];
  }

  // What's left in the tank once the named burns of the fuel plan have been made,
  // so the plan is stated once, as what each move costs.
  fuelLeftAfter(...phases) {
    return phases.reduce((left, phase) => left - this.fuelBurn[phase], 1);
  }

  // Fire the thrusters flat out for a set time, then put them out. The ship only
  // burns at the two moments it's actually changing its motion: leaving Earth, and
  // the orbit entry burn on a good capture.
  burnThrusters(durationSec) {
    this.killThrusterBurn();
    this.thrusterGlow.start();
    this.thrusterBurn = gsap.delayedCall(durationSec, () => {
      this.thrusterBurn = null;
      this.thrusterGlow.stop();
    });
  }

  // Cancel the end of a full burn the engine still has scheduled, so a reset
  // mid-burn can't fire it later.
  killThrusterBurn() {
    if (this.thrusterBurn) {
      this.thrusterBurn.kill();
      this.thrusterBurn = null;
    }
  }

  // Stop the main trip clock (planets/stats/gauges completion).
  killMainTween() {
    if (this.mainTween) {
      this.mainTween.kill();
      this.mainTween = null;
    }
  }

  // Stop the pending success-conclusion wait (e.g. a reset during the hold).
  clearConclusionTimeout() {
    if (this.conclusionTimeout) {
      clearTimeout(this.conclusionTimeout);
      this.conclusionTimeout = null;
    }
  }

  // The flight has landed. Keep the ship exactly where it ended (its final
  // position, rotation and shrunk scale stay as inline styles), and just clear the
  // aiming scaffolding: cancel any fill tween and drop the aiming class. The arc and
  // its filled trail are left on screen to show the trajectory that was flown; the
  // next trip's reset (prepareTrajectories / clearAim) hides them again. Unlike
  // clearAim this does NOT reset the ship, so it doesn't flip back and fade out at
  // Mars.
  finishFlight() {
    if (this.aimTween) {
      this.aimTween.kill();
      this.aimTween = null;
    }
    $(".ship").addClass("parked"); // stays visible once .aiming is removed
    $(".solar-system").removeClass("aiming");
    // The ship has run out of path with a capture still hanging: it can't get any
    // closer to Mars than it already has, so close the window out. In practice the
    // circle has long since decided it, this only catches a flight that somehow
    // ends first.
    if (this.rendezvous && !this.captureResolved && !this.captureWindowClosed) {
      this.closeCaptureWindow();
    }
    this.dragOriginX = null;
    this.dragOriginY = null;
    this.aimTurn = 0;
    this.aimVector = null;
    this.aimPercentage = 0;
  }

  // Called each frame of the flight with how far along the path the ship is (0 to
  // 1). Only a rendezvous runs the capture challenge: heads-up cues first, then the
  // tap window opening and closing with the circle.
  onTransferProgress(travel) {
    // Off the screen is the end of the run, whatever the launch was.
    if (this.checkShipLeftView()) return;

    // No rendezvous, no challenge: nothing is waiting at the meeting point, so the
    // ship just flies on by.
    if (!this.rendezvous || this.captureResolved || this.captureWindowClosed) return;

    // The cues are all about closing on Mars, so measure against reaching it rather
    // than against the path, which carries on well past the planet. This hits 1 as
    // the ship goes through the meeting point.
    const t = travel * this.approachProgressScale;

    // Heads-up, half-way out: the "prepare" instruction, well before the tap
    // window opens, so the player has time to read it.
    //
    // Half-way to Mars is measured against reaching the planet, not against the
    // path, and the path carries on well past it. So on a launch that passes Mars
    // early, half-way to it can come only moments after the ship leaves, and its
    // panel would cover the mission stats just as the player is reading what their
    // launch did. It waits out the opening seconds of the flight either way.
    const flownSec = travel * this.shipFlightSec;
    if (!this.captureInstructionShown
      && t >= this.captureConfig.instructionAt
      && flownSec >= this.captureConfig.instructionMinSec) {
      this.captureInstructionShown = true;
      this.showMissionInstruction("prepare");
    }

    // The target appears (not tappable yet), never before the instruction.
    if (!this.captureShown
      && this.captureInstructionShown
      && t >= this.captureConfig.showAt) {
      this.captureShown = true;
      this.showCaptureTarget();
    }

    // The window: both in the circle at once. Mars is only there for the moment it
    // takes to cross, and an early or late ship is elsewhere on the path while it
    // does. Taps are refused outside it.
    const inWindow = this.isCaptureWindowOpen(travel);

    if (!this.captureArmed && inWindow) {
      this.openCaptureWindow();
    } else if (this.captureArmed && !inWindow) {
      // One of them has left the circle with no tap: that was the whole chance.
      this.closeCaptureWindow();
    }
  }

  // Wrap the run up if the ship is no longer visible, and say whether it did. The
  // background blob clips every screen, so the ship is gone long before it reaches
  // the edge of the display and there's nothing left to watch. Every trip runs on
  // the same clock, so this and an upstream tank running dry (concludeWhenDry) are
  // the only things that end one early. A capture that can still be made is never
  // cut short.
  checkShipLeftView() {
    if (this.runEnded) return true;
    if (!this.solarSystem.isShipOutOfView(this.shipOffscreenMarginPx)) return false;
    if (this.rendezvous && !this.captureResolved && !this.captureWindowClosed) return false;

    this.endRunNow();
    return true;
  }

  // End the run at once on the conclusion it has already earned, stopping the trip
  // clock, the flight and the engine on the way.
  endRunNow() {
    this.runEnded = true;
    const name = this.pendingConclusion || this.launchConclusion || "wrong-trajectory";
    this.clearConclusionTimeout();
    this.killMainTween();
    this.solarSystem.killShipFlight();
    this.killThrusterBurn();
    this.thrusterGlow.stop();
    this.showConclusion(name);
  }

  // True while the ship and Mars are both inside the target circle, which is the
  // only time a capture can happen. `travel` is how far along the path the ship is
  // (0 to 1), the same number the flight reports.
  isCaptureWindowOpen(travel) {
    return this.solarSystem.isInDestination(this.solarSystem.aimPointAtProgress(travel))
      && this.solarSystem.isMarsInDestination();
  }

  // The ship and Mars have met inside the circle: taps count from here. The target
  // is normally already up, but show it here too so it can't be armed while hidden.
  openCaptureWindow() {
    this.captureArmed = true;
    if (!this.captureShown) {
      this.captureShown = true;
      this.showCaptureTarget();
    }
    this.armCaptureTarget();
  }

  // The window closed with no tap, so nothing pulls the ship in and it carries on
  // past Mars. Ends the trip itself: the window can close after the clock has run.
  closeCaptureWindow() {
    this.captureArmed = false;
    this.captureWindowClosed = true;
    this.hideCaptureTarget();
    this.showCaptureBlur("failed");
    this.showMissionInstruction("danger-missed");
    this.holdThenConclude("failed");
  }

  // The player tapped the capture target. Only counts while the window is open, so
  // any tap that gets here is one the ship could really have made.
  onCapturePress(e) {
    if (!this.captureArmed || this.captureResolved || this.captureWindowClosed) return;
    this.captureResolved = true;
    this.hideCaptureTarget();
    this.offsetCaptureBlurToMars();
    this.showCaptureBlur("success");
    this.showMissionInstruction("success");

    // Kill the arc flight and ease the ship onto Mars over landingSec. Keep it
    // parked and leave the arc and trail up to show the flown route.
    this.solarSystem.killShipFlight();
    // Orbit entry burn: the tap is what puts the ship on the right path, so the
    // engine fires for the whole way in and cuts out as it settles on Mars.
    this.burnThrusters(this.landingSec);
    // The deorbit burn costs the last of the planned fuel, over the same time as
    // the way down, so the gauge stops falling as the ship touches Mars.
    this.setFuel(this.fuelLeftAfter("launch", "cruise", "deorbit"), this.landingSec, "none");
    // That burn is what the last of the speed goes on, so the needle reaches 0 at
    // the same moment the ship comes to rest on the planet. Eased out, so it sheds
    // most of it early and slows gently into the stop.
    this.setSpeed(0, this.landingSec, "power1.out");
    // The counters were pacing themselves to a run that carried on past the capture
    // window; this one stops as soon as the ship is down and the conclusion is up,
    // so give them that clock instead and they arrive at their maximum with it.
    this.finishMissionCounters(this.landingSec + this.successConclusionSec);
    $(".ship").addClass("parked");
    $(".solar-system").removeClass("aiming");
    // The trail is left exactly as the flight drew it: it ends at the capture point,
    // where the ship left the path, so it shows the route actually flown rather than
    // drawing itself on to a place the ship never went along the arc. Only its very
    // end is softened, since a line that stops dead in open space reads as cut off.
    // This is the one ending where that happens: every other one runs off screen.
    this.solarSystem.fadeTrailTip("aim", this.trailTipFade, this.trailTipFadeSec);

    // The capture has done what the transfer was for, so the planets stop dead on
    // the sky the player was looking at when they pressed.
    this.solarSystem.freezePlanets();
    // Linear so the pace is steady the whole way in; a default ease front-loads it
    // and reads as a jump. Mars is stopped by now, so it's a run to a fixed point.
    // Shrinking to landingScale as it goes, the one place the ship gets smaller. The
    // landing owns the ending from here (onLandingComplete), not the trip clock.
    this.solarSystem.centerShipOnMars(
      this.landingSec,
      {x: this.landingScale, y: this.landingScale},
      () => this.onLandingComplete(),
      "none"
    );
  }

  // The ship has come to rest on Mars: hold, then fade the success conclusion in.
  // Timed from the landing rather than the trip clock because the window stays open
  // past that clock, and a burn made in that last stretch had nothing to schedule it.
  onLandingComplete() {
    this.clearConclusionTimeout();
    this.pendingConclusion = "success";
    this.conclusionTimeout = setTimeout(() => {
      this.conclusionTimeout = null;
      this.showConclusion("success");
    }, this.successConclusionSec * 1000);
  }

  // The trip has finished playing out, so it's time to say how it went. Only a
  // captured rendezvous brings anything to a stop; every other ending leaves the
  // planets going round and the ship flying while the explanation comes up.
  onTransferComplete() {
    // Never a rendezvous: the ship and Mars were never going to be in the same place
    // at the same time. resolveLaunch worked out which reason it was at launch, and
    // the trip has just played that out in front of the player, whether that was the
    // ship reaching the meeting point at the wrong moment or heading somewhere else
    // entirely. Give it a beat, then explain.
    if (!this.rendezvous) {
      this.holdThenConclude(this.launchConclusion || "wrong-trajectory");
      return;
    }

    // A real rendezvous: the capture challenge decides this one, and it runs on the
    // circle rather than on this clock, so it can still be live right now.
    // Tapped in time, so the ship is on its way down and the landing will say when
    // the scene is finished (see onLandingComplete). Nothing to do on this clock.
    if (this.captureResolved) return;

    // The window has already closed unmade, so closeCaptureWindow has said so and
    // set the ending going. Nothing left to do here.
    if (this.captureWindowClosed) return;

    // Mars is on the meeting point with no tap yet, so the window is at its
    // half-way mark: Mars is about to carry on out of the circle and take the
    // chance with it. Warn, and let the window closing decide how it ends.
    // (2026-08-28) Removed as requested by Juliette
    // if (!this.captureDangerShown) {
    //   this.captureDangerShown = true;
    //   this.showMissionInstruction("danger-off-track");
    // }
  }

  // Stop the whole scene dead, so the conclusion is read over a still picture of
  // the run rather than one that carries on behind it. Everything that moves is
  // driven from here: the trip clock, the planets, the ship, the engine and the
  // gauges and counters.
  freezeScene() {
    this.killMainTween();
    this.killStatsTimeline();
    this.solarSystem.killShipFlight();
    this.solarSystem.freezePlanets(); // also kills the tracked flight/landing tweens
    this.killThrusterBurn();
    this.thrusterGlow.stop();
    this.fuelGauge.kill();
    this.speedGauge.kill();
  }

  // When an upstream launch empties its tank, in seconds from launch. Never inside
  // the launch burn: the coast would have no time and the tank would empty in one
  // jump.
  upstreamDrySec(transferSec) {
    const launchSec = Math.min(this.shipLaunchBurnSec, transferSec);
    return Math.max(launchSec, transferSec * this.fuelBurnUpstream.dryAt);
  }

  // End the run upstreamDryConclusionSec after the tank runs dry, on the conclusion
  // the launch already earned. Shares the conclusion timer, so a reset or the ship
  // leaving the screen first (endRunNow) cancels it.
  concludeWhenDry(drySec) {
    this.clearConclusionTimeout();
    this.conclusionTimeout = setTimeout(() => {
      this.conclusionTimeout = null;
      this.endRunNow();
    }, (drySec + this.upstreamDryConclusionSec) * 1000);
  }

  // Wait failConclusionSec, then show a conclusion. Nothing is paused while it runs:
  // the planets keep going round and the ship keeps flying, so the scene carries on
  // without the player for a beat before it gets explained to them.
  holdThenConclude(name) {
    this.clearConclusionTimeout();
    this.pendingConclusion = name;
    this.conclusionTimeout = setTimeout(() => {
      this.conclusionTimeout = null;
      this.showConclusion(name);
    }, this.failConclusionSec * 1000);
  }

  // Cross-fade the solar system out and the ending graphic in. Success is the only
  // conclusion with a drawing, so there is just the one.
  showEndingGraphics() {
    gsap.to($(".solar-system"), {
      autoAlpha: 0,
      duration: 0.4,
      ease: "power1.out"
    });

    const $layer = $(".ending-graphics");
    const groups = this.endingGraphicPartGroups($layer);
    if (!groups.length) {
      // No parts marked up in this drawing, so fade the whole thing in at once.
      gsap.fromTo($layer,
        {autoAlpha: 0},
        {
          autoAlpha: 1,
          duration: 0.5,
          delay: 0.2,
          ease: "power1.out"
        });
      return;
    }

    // The layer itself is already up, and the groups come in one after the other on
    // top of it. Everything sharing a number fades together, as one step.
    gsap.set($layer, {autoAlpha: 1});
    const timeline = gsap.timeline({delay: this.endingPartDelaySec});
    groups.forEach(($group, index) => {
      timeline.fromTo($group,
        {autoAlpha: 0},
        {
          autoAlpha: 1,
          duration: this.endingPartFadeSec,
          ease: "power1.out"
        },
        index * this.endingPartStaggerSec);
    });
  }

  // The steps of an ending drawing's reveal, in the order given by the number in
  // their "ending-part-N" class: one entry per number, holding everything that
  // shares it. Tag the paths or groups in the SVG to opt a drawing in; anything left
  // untagged just fades in with the whole picture as before.
  endingGraphicPartGroups($layer) {
    const byNumber = new Map();
    $layer.find("[class*='ending-part-']").each((i, el) => {
      const match = /(?:^|\s)ending-part-(\d+)(?:\s|$)/.exec(el.getAttribute("class") || "");
      if (!match) return;
      const number = parseInt(match[1]);
      if (!byNumber.has(number)) byNumber.set(number, []);
      byNumber.get(number).push(el);
    });

    return [...byNumber.keys()].sort((a, b) => a - b).map((number) => $(byNumber.get(number)));
  }

  // Fade the active step out of the left info display. Uses alpha (not autoAlpha)
  // so it leaves no inline visibility behind for setupScreenSteps to clear on a
  // replay. The base fades steps the same way.
  hideCurrentStep() {
    // A quick press then release can launch while the step 2 -> 3 transition is
    // still running. Left alone that timeline keeps fading the incoming step in and
    // leaves it stuck on screen, so kill it and release the interaction lock it was
    // holding (its end callback, which unlocks, never runs once killed).
    if (this.app.stepTimeline) {
      this.app.stepTimeline.kill();
      this.app.stepTimeline = null;
      this.app.targetStep = null;
      this.app.unlockInteraction();
    }
    // Fade out every step, not just .current: mid-transition none is marked current
    // yet, so the incoming one would otherwise be missed.
    gsap.to($(".left-info-display .step"), {
      alpha: 0,
      duration: 0.4,
      ease: "power1.out"
    });
  }

  // Bring the speed gauge in (it starts hidden until launch).
  showSpeedGauge() {
    gsap.to(this.speedGauge.el, {
      autoAlpha: 1,
      duration: 0.4,
      ease: "power1.out"
    });
  }

  // Reveal the mission stats panel (hidden until launch) and fade each stat in and
  // up, one after another.
  showMissionStats() {
    const $stats = $(".mission-stats");
    gsap.set($stats, {autoAlpha: 1});

    // Every child but the instructions panel, which lives in here so it can sit
    // over the stats but is not one of them: it is an opaque black card and only
    // showMissionInstruction is allowed to raise it. Revealing it with the rest
    // would black the stats out from the moment of launch, on every path.
    gsap.fromTo($stats.children().not(".mission-instructions"),
      {
        autoAlpha: 0,
        y: 16
      },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.4,
        ease: "power2.out",
        stagger: 0.15
      });
  }

  // Animate the mission stats over the trip in one timeline: count the day and
  // delay counters up (data-min to data-max), move the two distance readouts (end
  // values depend on the arc), and fly the distance-graphic ship Earth to Mars
  // with the solid trail filling in behind it while the dashed track stays ahead.
  // Whether there's a distance worth reading out. A launch that meets Mars and one
  // thrown against the orbit are both pointed at the planet, so the gap closes and
  // the numbers mean something. Everything else gets the status message.
  readsDistance({rendezvous, upstream}) {
    return Boolean(rendezvous || upstream);
  }

  animateMissionStats(result, duration, countersSec = duration) {
    this.killStatsTimeline();
    const ease = "linear";
    const tl = gsap.timeline();
    this.statsTimeline = tl;

    // Counters: count between the data-min and data-max on each value span. They run
    // on countersSec rather than the trip clock, so they're still going when the
    // scene stops rather than reaching their maximum seconds early and waiting there.
    const $days = $(".days-value");
    const $delay = $(".delay-value");
    this.missionCounters = [
      this.addCounter(tl, $days, this.statMin($days), this.statMax($days), countersSec, ease),
      this.addCounter(tl, $delay, this.statMin($delay), this.statMax($delay), countersSec, ease),
    ];

    // Every launch sets the distance block's face for itself rather than trusting
    // what the last one left, so a retry straight after an out-of-range run gets its
    // numbers, its ship and a white track back even if it skipped a full reset.
    this.showDistanceReadouts();

    // Nothing out there to measure against, so the readouts and the ship crossing
    // the line make way for the status message and there's nothing else to animate.
    if (!this.readsDistance(result)) {
      this.runDistanceStatus(tl);
      return;
    }

    // Distance flown: counts up as the ship crosses.
    this.addCounter(tl, $(".distance-flown-value"), this.distanceFlown.start, this.distanceFlown.end, duration, ease, "M");

    // Ship: translate its group so its center rides the line from Earth to Mars.
    const {start, end: lineEnd, shipCenter} = this.distanceLine;
    tl.fromTo($(".mission-stats #distance-earth-mars-ship"),
      {x: start - shipCenter},
      {
        x: lineEnd - shipCenter,
        duration,
        ease
      }, 0);

    // Trail: reveal the solid line from Earth up to the ship over the same time.
    const trail = document.querySelector(".mission-stats .distance-trail");
    if (trail) {
      const len = trail.getTotalLength();
      trail.style.strokeDasharray = `${len}`;
      trail.style.strokeDashoffset = `${len}`; // hidden at the Earth end
      const state = {p: 0};
      tl.to(state, {
        p: 1,
        duration,
        ease,
        onUpdate: () => {
          trail.style.strokeDashoffset = `${len * (1 - state.p)}`;
        }
      }, 0);
    }
  }

  // Put the distance block on its readout face: number and ship back, status
  // message gone, track white again. Undoes runDistanceStatus, and runs on both a
  // reset and the start of every launch.
  showDistanceReadouts() {
    const $distances = $(".mission-stats .distances");
    $distances.removeClass("out-of-range");
    gsap.set($distances.find(".distance-flown-readout, #distance-earth-mars-ship, .distance-trail"), {autoAlpha: 1});
    gsap.set($distances.find(".distance-status, .distance-status-message"), {autoAlpha: 0});
  }

  // The distance block's other face, for a launch with no distance worth printing:
  // the readouts and the ship on the line go and a status message stands in, which
  // looks for the ship a moment, then gives up and turns the track orange. It rides
  // the stats timeline rather than its own timer, so killStatsTimeline cancels it
  // along with everything else in here.
  runDistanceStatus(tl) {
    const $distances = $(".mission-stats .distances");
    const $calculating = $distances.find(".distance-status-message.calculating");
    const $outOfRange = $distances.find(".distance-status-message.out-of-range");

    gsap.set($distances.find(".distance-flown-readout, #distance-earth-mars-ship, .distance-trail"), {autoAlpha: 0});
    gsap.set($distances.find(".distance-status"), {autoAlpha: 1});
    gsap.set($calculating, {autoAlpha: 1});

    // Every position here is absolute, because the counters have already made this
    // timeline the length of the whole run: anything appended without a position of
    // its own lands at the far end of it, seconds after the answer is wanted.
    const fadeSec = 0.25;
    const answerAt = this.distanceCalculatingSec + fadeSec;

    tl.to($calculating, {
      autoAlpha: 0,
      duration: fadeSec,
      ease: "power1.out"
    }, this.distanceCalculatingSec)
      .add(() => $distances.addClass("out-of-range"), answerAt)
      .to($outOfRange, {
        autoAlpha: 1,
        duration: fadeSec,
        ease: "power1.out"
      }, answerAt);
  }

  // Add a counting tween to `tl` at position 0: count the text of `$el` from `from`
  // to `to` over `duration`, rounded, with `suffix` stuck on the end.
  addCounter(tl, $el, from, to, duration, ease, suffix = "") {
    const counter = {$el, to, suffix, state: {v: from}};
    this.renderCounter(counter);
    tl.to(counter.state, {
      v: to,
      duration,
      ease,
      onUpdate: () => this.renderCounter(counter)
    }, 0);
    return counter;
  }

  renderCounter(counter) {
    counter.$el.text(Math.round(counter.state.v) + counter.suffix);
  }

  // Put the mission counters on a new clock, running them from wherever they've
  // reached to their maximum over `sec`. For the one ending nobody can schedule at
  // launch: a capture stops the scene landingSec later, whenever the player made it,
  // and the counters have to arrive with it rather than top out and sit there.
  // Only the counters move; the distance readouts and the graphic ship stay on the
  // trip clock, since the ship really does reach Mars when that clock runs out.
  finishMissionCounters(sec) {
    this.missionCounters.forEach((counter) => {
      // Takes the counter off the stats timeline as well as out of any earlier
      // handover, so the two can't both be writing to it.
      gsap.killTweensOf(counter.state);
      gsap.to(counter.state, {
        v: counter.to,
        duration: Math.max(0, sec),
        ease: "none",
        onUpdate: () => this.renderCounter(counter)
      });
    });
  }

  // The data-min / data-max range on a stat value span.
  statMin($el) {
    return parseFloat($el.data("min")) || 0;
  }

  statMax($el) {
    return parseFloat($el.data("max")) || 0;
  }

  // Reset the mission stats to their pre-launch state: counters at their min,
  // distances at the start, ship at the Earth end, trail hidden.
  resetMissionStats() {
    this.killStatsTimeline();

    const $days = $(".days-value");
    const $delay = $(".delay-value");
    $days.text(Math.round(this.statMin($days)));
    $delay.text(Math.round(this.statMin($delay)));
    $(".distance-flown-value").text(`${Math.round(this.distanceFlown.start)}M`);

    const {start, shipCenter} = this.distanceLine;
    gsap.set($(".mission-stats #distance-earth-mars-ship"), {x: start - shipCenter});

    this.showDistanceReadouts();

    // Undo a mission instruction having covered these (showMissionInstruction),
    // including one still fading them out when the run was cut short. The stats
    // panel itself is hidden here, and fades them all in again at launch.
    const $covered = $(".mission-stats").children(".communication-delay, .distances");
    gsap.killTweensOf($covered);
    gsap.set($covered, {clearProps: "opacity,visibility"});

    const trail = document.querySelector(".mission-stats .distance-trail");
    if (trail) {
      const len = trail.getTotalLength();
      trail.style.strokeDasharray = `${len}`;
      trail.style.strokeDashoffset = `${len}`;
    }
  }

  killStatsTimeline() {
    if (this.statsTimeline) {
      this.statsTimeline.kill();
      this.statsTimeline = null;
    }
    // A counter handed off to the landing's clock is running on its own tween by
    // then, which the timeline no longer owns, so stop those too.
    this.missionCounters.forEach((counter) => gsap.killTweensOf(counter.state));
    this.missionCounters = [];
  }

  // Stop everything moving, hide the HUD and fade the conclusion in over the scene
  // the run ended on. Success is the exception: it swaps the solar system out for
  // its own ending graphic.
  showConclusion(name) {
    this.freezeScene();

    gsap.to($(".left-info-display, .right-info-display"), {
      autoAlpha: 0,
      duration: 0.4,
      ease: "power1.out"
    });

    // Clear the capture overlay (the tap target with its glow, and the success/fail
    // glow) so it isn't left hanging over the conclusion.
    this.hideCaptureTarget();
    gsap.to($(".capture-blur"), {
      autoAlpha: 0,
      duration: 0.4,
      ease: "power1.out"
    });

    // Only success has a picture to show. The failures explain a launch the player
    // just watched, so the frozen system is left on screen to be talked about
    // instead of being replaced by a drawing of it.
    if (name === "success") this.showEndingGraphics();

    const $conclusions = $(".conclusions").show();
    const $target = $conclusions.find(`[data-conclusion="${name}"]`).show();
    $conclusions.find("[data-conclusion]").not($target).hide();

    gsap.fromTo($target,
      {
        autoAlpha: 0,
        y: 20
      },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.5,
        delay: 0.2,
        ease: "power1.out"
      });
  }

  hideTouchIndicator() {
    $(".touch-indicator").removeClass("active");
  }

  // Show the press, slide and lift hint on the orbit (placed in game.scss). The
  // WebP restarts from its first frame each time: Chromium keeps one animation
  // clock per image URL, so it gets a fresh one.
  showHandcoach() {
    const $hand = $(".handcoach");
    if (!$hand.length) return;

    $hand.attr("src", `${$hand.data("src-base")}?t=${Date.now()}`);

    gsap.killTweensOf($hand);
    gsap.to($hand, {
      autoAlpha: 1,
      duration: 0.2,
      ease: "power1.out"
    });
  }

  // Fade the hint out, or drop it at once (animate false) for a reset.
  hideHandcoach(animate = true) {
    const $hand = $(".handcoach");
    gsap.killTweensOf($hand);
    if (animate) {
      gsap.to($hand, {
        autoAlpha: 0,
        duration: 0.2,
        ease: "power1.out"
      });
    } else {
      gsap.set($hand, {autoAlpha: 0});
    }
  }

  // ---- Capture target (end-of-trip timing challenge) ----------------------

  // Park an HTML overlay on Mars's arrival point. Host-local -> page -> the
  // element's offset parent, matching showTouchIndicator so it lands right where
  // the ship arrives.
  positionOnMars($el) {
    const local = this.solarSystem.marsEndLocal();
    if (!$el.length || !local) return;
    const hostOffset = $(".solar-system").offset() || {left: 0, top: 0};
    const offset = $el.offsetParent().offset() || {left: 0, top: 0};
    $el.css({
      left: `${hostOffset.left + local.x - offset.left}px`,
      top: `${hostOffset.top + local.y - offset.top}px`,
    });
  }

  // Bring the capture indicator up on Mars as a heads-up (visible but not yet
  // tappable; armCaptureTarget enables taps). Parking the wrapper on Mars places
  // both it and the (still hidden) success/fail blur, which is ready to show the
  // moment the challenge resolves.
  showCaptureTarget() {
    this.positionOnMars($(".capture"));
    const $ind = $(".capture-touch-indicator");
    if (!$ind.length) return;

    $ind.off("pointerdown", this.onCapturePress).on("pointerdown", this.onCapturePress);
    gsap.fromTo($ind,
      {autoAlpha: 0},
      {
        autoAlpha: 1,
        duration: 0.25,
        ease: "power1.out",
        onComplete: () => this.refreshGlass($ind) // fix the backdrop-filter repaint
      });
  }

  // Make the already-shown capture target tappable (the ship has reached the
  // circle). The .active class flips pointer-events on so the press registers.
  armCaptureTarget() {
    $(".capture-touch-indicator").addClass("active");
  }

  // Take the capture target down: stop listening and fade it out.
  hideCaptureTarget() {
    const $ind = $(".capture-touch-indicator").off("pointerdown", this.onCapturePress).removeClass("active");
    gsap.to($ind, {
      autoAlpha: 0,
      duration: 0.2,
      ease: "power1.out"
    });
  }

  // Move the outcome glow onto Mars. The .capture overlay is parked on the circle's
  // centre, but Mars is only somewhere inside that circle when the burn is made, so
  // without this the glow sits up to a radius from where the ship lands.
  // left/top rather than a GSAP x/y: the glow is centred with a percentage translate
  // and GSAP would read that resolved matrix as a pixel offset of its own.
  offsetCaptureBlurToMars() {
    const now = this.solarSystem.marsCurrentLocal();
    const centre = this.solarSystem.marsEndLocal();
    if (!now || !centre) return;
    $(".capture-blur").css({
      left: `calc(50% + ${now.x - centre.x}px)`,
      top: `calc(50% + ${now.y - centre.y}px)`,
    });
  }

  // Swap the capture indicator for its outcome glow (success / failed), the way
  // the planet targets show blur-planet-in.png. JS already parked it on Mars.
  // Success dissipates (see captureSuccessGlow), failed just fades in and stays.
  showCaptureBlur(kind) {
    const $blur = $(".capture-blur").removeClass("success failed").addClass(kind);
    gsap.killTweensOf($blur);

    if (kind !== "success") {
      gsap.set($blur, {"--capture-blur-scale": 1});
      gsap.to($blur, {
        autoAlpha: 1,
        duration: 0.3,
        ease: "power1.out"
      });
      return;
    }

    // The scale goes through a CSS variable since the glow is centred with a
    // percentage translate that GSAP would read as a pixel offset.
    const {growSec, fadeInSec, fadeOutAt} = this.captureSuccessGlow;
    const fadeOutDelay = growSec * fadeOutAt;

    gsap.fromTo($blur,
      {"--capture-blur-scale": 0},
      {
        "--capture-blur-scale": 1,
        duration: growSec,
        ease: "expo.out"
      });

    gsap.fromTo($blur,
      {autoAlpha: 0},
      {
        autoAlpha: 1,
        duration: fadeInSec,
        ease: "power1.out"
      });

    gsap.to($blur, {
      autoAlpha: 0,
      duration: growSec - fadeOutDelay,
      delay: fadeOutDelay,
      ease: "power1.in"
    });
  }

  // Hide the outcome glow and clear its variant (for a reset / new trip).
  hideCaptureBlur() {
    const $blur = $(".capture-blur");
    gsap.killTweensOf($blur);
    gsap.set($blur, {
      autoAlpha: 0,
      "--capture-blur-scale": 1
    });
    $blur.css({left: "", top: ""}); // drop any nudge onto Mars from the last trip
    $blur.removeClass("success failed");
  }

  // Cross-fade to one mission instruction (prepare / success / danger-off-track /
  // danger-missed), fading any other out. Reveals the container (black panel) the
  // first time one shows; it stays up through the swaps until the trip resets.
  showMissionInstruction(name) {
    const $wrap = $(".mission-instructions");
    const $target = $wrap.find(`[data-instruction="${name}"]`);
    const $others = $wrap.find("[data-instruction]").not($target);

    // Kill any in-flight instruction fades first. Capturing just after the
    // danger-off-track warning appeared would otherwise leave its fade-in tween
    // running, fighting this success fade-out and flashing danger back over success.
    gsap.killTweensOf($wrap.find("[data-instruction]"));

    // The panel covers the communication delay and distance flown, so they go while
    // it's up (mission days stays, it's clear of it). Once gone they stay gone:
    // every instruction after "prepare" swaps in over the same spot, and
    // showMissionStats brings them back on the next launch. Killed first in case
    // the launch stagger is still bringing them in.
    const $covered = $(".mission-stats").children(".communication-delay, .distances");
    gsap.killTweensOf($covered);
    gsap.to($covered, {
      autoAlpha: 0,
      duration: 0.3,
      ease: "power1.out"
    });

    gsap.to($wrap, {
      autoAlpha: 1,
      duration: 0.3,
      ease: "power1.out"
    });
    gsap.to($others, {
      autoAlpha: 0,
      duration: 0.2,
      ease: "power1.out"
    });
    gsap.fromTo($target,
      {
        autoAlpha: 0,
        y: 12
      },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.3,
        ease: "power1.out"
      });
  }

  // Hide the whole mission-instructions panel and every instruction inside it (no
  // fade). Kills any in-flight fade first so a restart mid-cross-fade can't leave
  // one creeping back in.
  hideMissionInstructions() {
    const $wrap = $(".mission-instructions");
    const $all = $wrap.find("[data-instruction]");
    gsap.killTweensOf($wrap);
    gsap.killTweensOf($all);
    gsap.set($wrap, {autoAlpha: 0});
    gsap.set($all, {autoAlpha: 0});
  }

  // Stop an in-progress error flash and clear its red state, so a correct press
  // can proceed cleanly even while the error is still on screen. The retroaction
  // and instructions ride the step out when goToNextStep fades it, and reset on the
  // next entry to step 2.
  clearErrorFlash() {
    if (this.flashTimeline) {
      this.flashTimeline.kill();
      this.flashTimeline = null;
    }
    $(".solar-system").removeClass("flash-error");
    $(".planet-target").removeClass("error");
  }

  // Turn the orbit lines and target borders red, swap the instructions out for the
  // "wrong" retroaction, hold for flashErrorMs, then ease everything back. Presses
  // are refused at first but re-enabled after errorReenableMs, well before the
  // flash finishes, so the player can try again while it's still showing (a correct
  // press then clears the flash, see clearErrorFlash / onPress).
  flashError() {
    this.acceptPress = false;
    const swap = 0.3;

    if (this.flashTimeline) this.flashTimeline.kill();
    this.flashTimeline = gsap.timeline({
      onComplete: () => {
        this.flashTimeline = null;
      },
    });

    this.flashTimeline
      // Turn everything red and fade the instructions out for the retroaction (which
      // fades up as it appears).
      .add(() => {
        $(".solar-system").addClass("flash-error");
        $(".planet-target").addClass("error");
      }, 0)
      .to($(".step-instructions"), {
        autoAlpha: 0,
        duration: swap,
        ease: "power1.out"
      })
      .fromTo($(".step-retroaction-wrong"),
        {
          autoAlpha: 0,
          y: 20,
          yPercent: -50
        },
        {
          autoAlpha: 1,
          y: 0,
          duration: swap,
          ease: "power1.out"
        })
      // Let the player try again well before the flash eases off.
      .add(() => {
        this.acceptPress = true;
      }, this.errorReenableMs / 1000)
      // Hold the error, then clear the red flash.
      .add(() => {
        $(".solar-system").removeClass("flash-error");
        $(".planet-target").removeClass("error");
      }, this.flashErrorMs / 1000)
      // Hide the retroaction and bring the instructions back.
      .to($(".step-retroaction-wrong"), {
        autoAlpha: 0,
        duration: swap,
        ease: "power1.out"
      })
      .to($(".step-instructions"), {
        autoAlpha: 1,
        duration: swap,
        ease: "power1.out"
      })
      // And the hand hint with them, to show the gesture again. A press before
      // this point kills the timeline (a new flash, or clearErrorFlash on a good
      // one), so it never comes back over a press already made.
      .add(() => this.showHandcoach(), "<");
  }

  // Clear the error and aligned looks, the lock/launch flags, and the aim state.
  clearStates() {
    this.enableTouchActions = false;
    this.hideHandcoach(false);
    this.cancelTargetRingHint();
    this.holdScreenSaver(false);
    // A restart can come from a second finger while the first is still down
    // aiming ("Restart game" is up from step 3). Let go of that finger, so the
    // fresh step 2 takes presses again, and take its touch circle down with it.
    this.activePointerId = null;
    this.hideTouchIndicator();
    // Presses only get accepted once the player reaches step 2 (initStep enables
    // it), so a reset must turn it back off. Step 1 must not accept presses.
    this.acceptPress = false;
    this.locked = false;
    this.launched = false;
    this.chosenPath = null;
    this.rendezvous = false;
    this.launchConclusion = null;
    // A restart can land mid-flash (a wrong-alignment press on step 2), so stop it.
    // Otherwise it would keep toggling classes and re-enable presses at its end.
    if (this.flashTimeline) {
      this.flashTimeline.kill();
      this.flashTimeline = null;
    }
    this.killStatsTimeline(); // stop the mission-stats trip animation on a restart
    this.clearTargetFade();
    // Drop any in-progress capture challenge (target, listener, flags, instructions).
    this.captureInstructionShown = false;
    this.captureShown = false;
    this.captureArmed = false;
    this.captureResolved = false;
    this.captureWindowClosed = false;
    this.captureDangerShown = false;
    this.runEnded = false;
    this.pendingConclusion = null;
    this.killMainTween();              // stop the trip clock on a restart
    this.clearConclusionTimeout();     // and any pending success conclusion
    this.solarSystem.killShipFlight(); // and the ship flight
    // Engine out, and drop the pending end of a burn so one interrupted by a
    // restart can't fire its stop later over a fresh launch.
    this.killThrusterBurn();
    this.thrusterGlow.reset();
    // The ship only shows once step 2 puts it back on the Earth target.
    $(".solar-system").removeClass("ship-idle");
    this.hideCaptureTarget();
    this.hideCaptureBlur();
    this.hideMissionInstructions();
    $(".solar-system").removeClass("flash-error");
    $(".planet-target").removeClass("error aligned");
    this.clearAim();
  }

  // Cancel the pending targets fade-out timer.
  clearTargetFade() {
    if (this.targetFadeTimeout) {
      clearTimeout(this.targetFadeTimeout);
      this.targetFadeTimeout = null;
    }
  }

  // Re-apply the liquid glass on the matching elements once they're shown. A
  // GSAP opacity fade (on the element or an ancestor) suppresses the
  // backdrop-filter in Chromium, and it doesn't repaint on its own once opacity
  // is back to 1 (which is why the planet-target glass only appeared once the
  // .error color transition forced a repaint). Re-setting the filter here forces
  // that repaint.
  refreshGlass($els) {
    const glasses = (this.app && this.app.liquidGlass) || [];
    glasses.forEach((g) => {
      if (!g.el || !$els.is(g.el)) return;
      g.el.style.backdropFilter = "none";
      g.el.style.webkitBackdropFilter = "none";
      void g.el.offsetWidth; // force a reflow so the re-set actually repaints
      g.apply();
    });
  }

  // Fade the targets out. Goes through GSAP because initStep set an inline
  // opacity via autoAlpha, which a CSS class could not override.
  fadeTargetsOut() {
    gsap.to($(".planet-target"), {
      autoAlpha: 0,
      duration: 0.4,
      ease: "power1.out"
    });
  }

  // Take every trajectory off screen. Call once each time we enter, so a path left
  // over from the last trip isn't still up.
  prepareTrajectories() {
    $(".trajectory").each((i, el) => el.classList.remove("is-active"));
    this.solarSystem.showDestination(false);
  }

  // Stop aiming: ship out, path hidden, drag and aim state cleared.
  clearAim() {
    if (this.aimTween) {
      this.aimTween.kill(); // cancel a pending snap so it can't launch later
      this.aimTween = null;
    }
    $(".solar-system").removeClass("aiming");
    $(".ship").removeClass("parked").css("transform", ""); // back to CSS centering
    this.dragOriginX = null;
    this.dragOriginY = null;
    this.aimTurn = 0;
    this.aimVector = null;
    this.aimPercentage = 0;
    this.cancelTargetRingHint();
    this.solarSystem.hideAimPath();
    this.solarSystem.showDestination(false);
    this.solarSystem.clearTrails();
  }

  // Keep the screensaver off while a finger is down. A finger held still while the
  // player weighs up a path sends no touch events, so the idle timer would
  // otherwise run out mid-aim. Releasing restarts the timer from zero. Only undoes
  // its own pause, so it can't unpause one made elsewhere.
  holdScreenSaver(on) {
    if (on && !this.screenSaverHeld) {
      this.screenSaverHeld = true;
      this.app.pauseScreenSaver();
    } else if (!on && this.screenSaverHeld) {
      this.screenSaverHeld = false;
      this.app.unpauseScreenSaver();
    }
  }

  // Start the countdown to the target circle hint, from the moment the drag starts.
  scheduleTargetRingHint() {
    this.cancelTargetRingHint();
    this.targetRingHintCall = gsap.delayedCall(this.targetRingHintSec, () => {
      this.targetRingHintCall = null;
      this.solarSystem.setTargetRingRevealed(true);
      // Redraw through the normal path so the circle comes in already reading the
      // current aim, even if the finger is holding still.
      this.updateAim();
    });
  }

  // Work out the zone of launches that would meet Mars: walk the whole drag, every
  // direction against every force, ask aimWouldRendezvous about each, and let the
  // solar system outline the ones that pass. Asking the same question the release
  // does means the zone can never disagree with the game.
  //
  // It can't happen at page load: the target circle is sized in screen pixels and
  // turned into SVG units through the SVG's place on screen, which the hidden game
  // screen doesn't have yet. The first start is the earliest it's laid out, and
  // still well before the first press, so the work never lands mid-aim.
  buildGoodZone() {
    this.solarSystem.setDestinationRadius(this.targetToleranceRadius());
    const travel = this.rendezvousTravelDistance();
    const geos = [];

    for (let i = 0; i <= this.goodZoneTurnSteps; i++) {
      const aim = this.solarSystem.aimVectorFor(-1 + (2 * i) / this.goodZoneTurnSteps);
      for (let j = 0; j <= this.goodZoneForceSteps; j++) {
        const force = j / this.goodZoneForceSteps;
        if (this.aimWouldRendezvous(aim, force)) geos.push(this.solarSystem.aimGeometry(aim.x, aim.y, force));
      }
    }

    this.solarSystem.setGoodZone(geos, travel);
    this.goodZoneBuilt = true;
  }

  // Drop a pending hint and take the circle back off, for the next attempt.
  cancelTargetRingHint() {
    if (this.targetRingHintCall) {
      this.targetRingHintCall.kill();
      this.targetRingHintCall = null;
    }
    this.solarSystem.setTargetRingRevealed(false);
  }

  // Set the fuel gauge level (0–1), optionally over a given duration/ease.
  setFuel(level, duration, ease) {
    if (this.fuelGauge) this.fuelGauge.setLevel(level, true, duration, ease);
  }

  // Set the speed gauge level (0–1), optionally over a given duration/ease.
  setSpeed(level, duration, ease) {
    if (this.speedGauge) this.speedGauge.setLevel(level, true, duration, ease);
  }

  reset() {
    this.gameFrame.hide();
    this.clearStates();
    this.solarSystem.reset();
    this.fuelGauge.setLevel(1, false); // full
    this.speedGauge.setLevel(0, false); // 0 km/s
    gsap.set($(".top-bar"), {alpha: 1});
  }
}
